import { DefaultButton, Icon, IconButton, Modal, Stack, Text } from '@fluentui/react';
import { useId } from '@uifabric/react-hooks';
import DynamicForm from 'components/DynamicForm/DynamicForm';
import IvicosStrings from 'kits/language/stringKit';
import React, { useEffect, useState } from 'react';

import AegeanSeaImg from '../../../media/img/dashboard-bg-aegean-sea.jpg';
import EuropeanSquareImg from '../../../media/img/dashboard-bg-european-square.jpg';
import TimberTownImg from '../../../media/img/dashboard-bg-timber-town.jpg';
import RollingHillsImg from '../../../media/img/dashboard-bg-rolling-hills.jpg';

const DASHBOARD_BACKGROUNDS = [
    { id: 0, name: 'Aegean Sea', src: AegeanSeaImg },
    { id: 1, name: 'European Square', src: EuropeanSquareImg },
    { id: 2, name: 'Timber Town', src: TimberTownImg },
    { id: 3, name: 'Rolling Hills', src: RollingHillsImg }
];

interface IBrandIdentityProps {
    campusFormFields: any[];
    visitorFormFields: any[];
    onCampusFormChange: (key: string, value: any) => void;
    onVisitorFormChange: (key: string, value: any) => void;
    dashboardBgImage?: number;
}

interface IFloatingCardProps {
    opacity?: number;
    onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
    onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
    left?: string;
}

const FloatingCard: React.FC<IFloatingCardProps> = ({ opacity = 1, onMouseEnter, onMouseLeave, left = '25%' }) => (
    <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{
            position: 'absolute',
            top: '50%',
            left: left,
            transform: 'translateY(-50%)',
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(8px)',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            padding: '10px 14px',
            opacity,
            transition: 'opacity 0.2s ease',
            minWidth: 160,
            height: 100,
            zIndex: 2
        }}
    >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <Icon iconName="GridViewSmall" styles={{ root: { fontSize: 14 } }} />
            <span style={{ fontSize: 13 }}>{IvicosStrings.outlookWidgetInboxTitle}</span>
        </div>
        <div style={{ fontSize: 12, marginBottom: 2 }}>• Elena Rossi</div>
        <div
            style={{
                fontSize: 12,
                color: '#9CA3AF',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 140
            }}
        >
            Q2 roadmap...
        </div>
    </div>
);

const BrandIdentity: React.FC<IBrandIdentityProps> = ({ campusFormFields, visitorFormFields, onCampusFormChange, onVisitorFormChange, dashboardBgImage }) => {
    const [selectedBgIndex, setSelectedBgIndex] = useState<number>(dashboardBgImage ?? 0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPreviewCardHovered, setIsPreviewCardHovered] = useState(false);
    const [cardHovered, setCardHovered] = useState(false);
    const dashboardModalTitleId = useId('dashboard-bg-title');

    useEffect(() => {
        setSelectedBgIndex(dashboardBgImage ?? 0);
    }, [dashboardBgImage]);

    const handleSelectBg = (id: number) => {
        setSelectedBgIndex(id);
        onCampusFormChange('dashboard_background_image', id);
    };

    const selectedBg = DASHBOARD_BACKGROUNDS[selectedBgIndex] ?? DASHBOARD_BACKGROUNDS[0];

    return (
        <Stack tokens={{ childrenGap: 24 }} style={{ height: 500, overflowY: 'auto', overflowX: 'hidden', padding: '20px 0' }}>
            <Stack style={{ width: '97%', margin: '0 auto' }}>
                <Text variant="large" style={{ marginBottom: 16, fontWeight: 600, borderBottom: '2px solid #000' }}>
                    {IvicosStrings.campusDesignTitle}
                </Text>
                <DynamicForm formFields={campusFormFields} onChange={onCampusFormChange} />
            </Stack>

            <Stack horizontal style={{ width: '97%', margin: '20px auto' }}>
                <Stack verticalAlign="center" tokens={{ childrenGap: 6 }}>
                    <Text style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 16, display: 'block' }}>
                        {IvicosStrings.dashboardBackgroundImageTitle}
                    </Text>

                    <div style={{ width: '90%', height: 240, overflow: 'hidden', position: 'relative' }}>
                        <img src={selectedBg.src} alt={selectedBg.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        <FloatingCard
                            opacity={isPreviewCardHovered ? 1 : 0.7}
                            onMouseEnter={() => setIsPreviewCardHovered(true)}
                            onMouseLeave={() => setIsPreviewCardHovered(false)}
                        />
                    </div>
                </Stack>

                <Stack verticalAlign="center" style={{ marginTop: 12, marginLeft: 30 }}>
                    <DefaultButton
                        iconProps={{ iconName: 'Edit' }}
                        text={IvicosStrings.changeImageActionTitle}
                        onClick={() => setIsModalOpen(true)}
                        styles={{ root: { background: 'white', border: '1px solid #374151', width: 160, height: 35 } }}
                    />
                </Stack>
            </Stack>

            <Modal isOpen={isModalOpen} onDismiss={() => setIsModalOpen(false)} isBlocking={false} titleAriaId={dashboardModalTitleId}>
                <div style={{ background: 'white', padding: 24, width: 560, maxWidth: '90vw', position: 'relative' }}>
                    <IconButton
                        iconProps={{ iconName: 'Cancel' }}
                        ariaLabel={IvicosStrings.closeActionTitle}
                        onClick={() => setIsModalOpen(false)}
                        styles={{
                            root: {
                                position: 'absolute',
                                top: 8,
                                right: 8
                            }
                        }}
                    />
                    <Text id={dashboardModalTitleId} style={{ fontWeight: 700, fontSize: 18, color: '#111827', display: 'block', marginBottom: 6 }}>
                        {IvicosStrings.dashboardBackgroundModalTitle}
                    </Text>
                    <Text style={{ fontSize: 14, color: '#6B7280', display: 'block', marginBottom: 20 }}>{IvicosStrings.dashboardBackgroundModalSubtitle}</Text>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {DASHBOARD_BACKGROUNDS.map((bg) => (
                            <div
                                key={bg.id}
                                onClick={() => handleSelectBg(bg.id)}
                                style={{ position: 'relative', aspectRatio: '16 / 9', borderRadius: 8, overflow: 'hidden', cursor: 'pointer' }}
                            >
                                <img src={bg.src} alt={bg.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                <div
                                    style={{
                                        position: 'absolute',
                                        bottom: 8,
                                        left: 8,
                                        background: 'rgba(0,0,0,0.5)',
                                        color: 'white',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        borderRadius: 4,
                                        padding: '4px 8px'
                                    }}
                                >
                                    {bg.name}
                                </div>
                                {selectedBgIndex === bg.id && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            background: '#1F2937',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Icon iconName="CheckMark" styles={{ root: { color: 'white', fontSize: 14 } }} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <Text style={{ fontWeight: 700, fontSize: 14, color: '#111827', display: 'block', marginTop: 16, marginBottom: 12 }}>
                        {`${IvicosStrings.dashboardBackgroundCurrentSelection}: ${selectedBg.name}`}
                    </Text>

                    <div style={{ width: '100%', height: 240, overflow: 'hidden', position: 'relative' }}>
                        <img src={selectedBg.src} alt={selectedBg.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        <div onMouseEnter={() => setCardHovered(true)} onMouseLeave={() => setCardHovered(false)} style={{ position: 'absolute', inset: 0 }}>
                            {/* <FloatingCard opacity={cardHovered ? 0.4 : 1} /> */}
                            <FloatingCard
                                opacity={cardHovered ? 1 : 0.7}
                                onMouseEnter={() => setCardHovered(true)}
                                onMouseLeave={() => setCardHovered(false)}
                                left="35%"
                            />
                        </div>
                    </div>
                </div>
            </Modal>

            <Stack style={{ width: '97%', margin: '0 auto' }}>
                <Text variant="large" style={{ marginBottom: 16, fontWeight: 600, borderBottom: '2px solid #000' }}>
                    {IvicosStrings.visitorEntranceDesignTitle}
                </Text>
                <DynamicForm formFields={visitorFormFields} onChange={onVisitorFormChange} />
            </Stack>
        </Stack>
    );
};

export default BrandIdentity;
